import BlockIcon from "@mui/icons-material/Block";
import Popper from "@mui/material/Popper";
import { VirtualElement } from "@popperjs/core";
import classNames from "classnames";
import {
  MouseEventHandler,
  ReactNode,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  applyForeColor,
  applyInlineFormat,
  getActiveInlineFormats,
  ActiveInlineFormats,
  COLOR_FORMATTING_ENABLED,
  ENABLED_INLINE_FORMATS,
  InlineFormatCommand,
} from "./RichTextFormattingCommands";

interface RichTextSelectionToolbarProps {
  editorRef: RefObject<HTMLDivElement>;
  /** Called after a formatting command mutated the editor DOM. */
  onApplied: () => void;
}

// Hex values behind the design system tokens (tailwind.config.js /
// src/style.css); the stored format needs concrete hex, not CSS variables.
const COLOR_PRESETS: { name: string; label: string; hex: string | null }[] = [
  { name: "default", label: "Default", hex: null },
  { name: "green", label: "Green", hex: "#8fbc8f" }, // interaction.correct (--pi-game-green)
  { name: "red", label: "Red", hex: "#d35f5f" }, // interaction.false
  { name: "orange", label: "Orange", hex: "#f1691e" }, // interaction.secondary (--pi-game-orange)
];

const preventFocusSteal: MouseEventHandler = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

function ToolbarButton({
  label,
  active,
  onSelect,
  children,
}: {
  label: string;
  active?: boolean;
  onSelect: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      className={classNames(
        "flex h-[28px] min-w-[28px] items-center justify-center rounded-[6px] px-[6px] text-[14px] text-creator-text-sub hover:bg-creator-primary-background",
        active && "bg-creator-primary-washed text-creator-primary"
      )}
      onMouseDown={preventFocusSteal}
      onClick={onSelect}
    >
      {children}
    </button>
  );
}

/**
 * Floating formatting toolbar that appears above a non-collapsed selection
 * inside the rich text editor. Buttons prevent default on mousedown so the
 * text selection (and editor focus) survives clicking them.
 */
export default function RichTextSelectionToolbar({
  editorRef,
  onApplied,
}: RichTextSelectionToolbarProps) {
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const [activeFormats, setActiveFormats] = useState<ActiveInlineFormats>({
    bold: false,
    italic: false,
    underline: false,
    strike: false,
  });
  const [openMenu, setOpenMenu] = useState<"color" | null>(null);
  const [lastColor, setLastColor] = useState("#8fbc8f");
  const savedRangeRef = useRef<Range | null>(null);
  const openMenuRef = useRef<typeof openMenu>(null);
  openMenuRef.current = openMenu;

  useEffect(() => {
    const handleSelectionChange = () => {
      const editor = editorRef.current;
      const selection = window.getSelection();

      if (!editor || !selection || selection.rangeCount === 0 || selection.isCollapsed) {
        // While a picker menu is open the selection may collapse (e.g. the
        // color picker grabs the pointer); keep the toolbar up until the menu
        // interaction finishes.
        if (!openMenuRef.current) {
          setAnchorRect(null);
          setOpenMenu(null);
        }
        return;
      }

      const range = selection.getRangeAt(0);
      if (!editor.contains(range.commonAncestorContainer)) {
        if (!openMenuRef.current) {
          setAnchorRect(null);
          setOpenMenu(null);
        }
        return;
      }

      savedRangeRef.current = range.cloneRange();
      // jsdom does not implement getBoundingClientRect on Range.
      setAnchorRect(
        typeof range.getBoundingClientRect === "function"
          ? range.getBoundingClientRect()
          : editor.getBoundingClientRect()
      );
      setActiveFormats(getActiveInlineFormats(editor, range));
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () => document.removeEventListener("selectionchange", handleSelectionChange);
  }, [editorRef]);

  const anchorEl: VirtualElement | null = useMemo(() => {
    if (!anchorRect) return null;
    return {
      contextElement: editorRef.current ?? undefined,
      getBoundingClientRect: () => anchorRect,
    };
  }, [anchorRect, editorRef]);

  const restoreSelection = () => {
    const editor = editorRef.current;
    const saved = savedRangeRef.current;
    const selection = window.getSelection();
    if (!editor || !saved || !selection) return;
    if (!editor.contains(saved.commonAncestorContainer)) return;

    editor.focus();
    selection.removeAllRanges();
    selection.addRange(saved);
  };

  const refreshActiveFormats = () => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) return;
    setActiveFormats(getActiveInlineFormats(editor, selection.getRangeAt(0)));
  };

  const handleInlineFormat = (command: InlineFormatCommand) => {
    restoreSelection();
    applyInlineFormat(command);
    onApplied();
    refreshActiveFormats();
  };

  const handleColor = (colorHex: string | null) => {
    const editor = editorRef.current;
    if (!editor) return;
    restoreSelection();
    applyForeColor(editor, colorHex);
    onApplied();
    setOpenMenu(null);
  };

  if (!anchorEl) return null;

  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement="top"
      modifiers={[{ name: "offset", options: { offset: [0, 12] } }]}
      style={{ zIndex: 1400 }}
    >
      <div
        data-testid="rich-text-toolbar"
        className="flex flex-col items-center"
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="relative z-10 flex items-center gap-[2px] rounded-[10px] border-[1px] border-creator-border bg-creator-surface p-[4px] shadow-lg">
          <ToolbarButton
            label="rich-text-bold"
            active={activeFormats.bold}
            onSelect={() => handleInlineFormat("bold")}
          >
            <span className="font-bold">B</span>
          </ToolbarButton>
          <ToolbarButton
            label="rich-text-italic"
            active={activeFormats.italic}
            onSelect={() => handleInlineFormat("italic")}
          >
            <span className="italic">I</span>
          </ToolbarButton>
          {ENABLED_INLINE_FORMATS.underline && (
            <ToolbarButton
              label="rich-text-underline"
              active={activeFormats.underline}
              onSelect={() => handleInlineFormat("underline")}
            >
              <span className="underline">U</span>
            </ToolbarButton>
          )}
          {ENABLED_INLINE_FORMATS.strikeThrough && (
            <ToolbarButton
              label="rich-text-strikethrough"
              active={activeFormats.strike}
              onSelect={() => handleInlineFormat("strikeThrough")}
            >
              <span className="line-through">S</span>
            </ToolbarButton>
          )}
          {COLOR_FORMATTING_ENABLED && (
            <>
              <div className="mx-[2px] h-[20px] w-[1px] bg-creator-border" />
              <div className="relative">
                <ToolbarButton
                  label="rich-text-color"
                  active={openMenu === "color"}
                  onSelect={() => setOpenMenu(openMenu === "color" ? null : "color")}
                >
                  <span className="font-semibold underline" style={{ color: lastColor }}>
                    A
                  </span>
                </ToolbarButton>
                {openMenu === "color" && (
                  <div
                    data-testid="rich-text-color-menu"
                    className="absolute bottom-full left-1/2 mb-[10px] flex -translate-x-1/2 items-center gap-[6px] rounded-[10px] border-[1px] border-creator-border bg-creator-surface p-[6px] shadow-lg"
                    onMouseDown={preventFocusSteal}
                  >
                    {COLOR_PRESETS.map((preset) => (
                      <button
                        key={preset.name}
                        type="button"
                        aria-label={`rich-text-color-${preset.name}`}
                        title={preset.label}
                        className="flex h-[20px] w-[20px] items-center justify-center rounded-full border-[1px] border-creator-border hover:opacity-80"
                        style={preset.hex ? { backgroundColor: preset.hex } : undefined}
                        onMouseDown={preventFocusSteal}
                        onClick={() => {
                          if (preset.hex) setLastColor(preset.hex);
                          handleColor(preset.hex);
                        }}
                      >
                        {!preset.hex && (
                          <BlockIcon sx={{ fontSize: 16 }} className="text-creator-text-sub" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div
          data-testid="rich-text-toolbar-arrow"
          className="relative z-20 -mt-[6px] h-[11px] w-[11px] rotate-45 rounded-[2px] border-b-[1px] border-r-[1px] border-creator-border bg-creator-surface"
        />
      </div>
    </Popper>
  );
}
